import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';

import { ArtifactUnpacked, Message } from '@/services/types/artifact-types';

import { ConversationHeader } from './ConversationHeader';
import { ConversationStats } from './ConversationStats';
import { MessageList } from './MessageList';
import { MessageSearch } from './MessageSearch';

interface ConversationViewerProps {
  artifact: ArtifactUnpacked;
}

/**
 * ConversationViewer - Displays SMS/Email conversation threads with search and filtering
 * Follows component decomposition guidelines with focused sub-components
 */
export const ConversationViewer = ({ artifact }: ConversationViewerProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMessageType, setSelectedMessageType] = useState<string>('all');

  // Type guard to ensure we have a conversation artifact
  const conversation =
    artifact.artifact_type === 'CONVERSATION' ? artifact.conversation : null;

  // Derived data
  const { filteredMessages, messageStats } = useMemo(() => {
    if (!conversation?.messages) {
      return { filteredMessages: [], messageStats: {} };
    }

    const { messages: initialMessages } = conversation;
    let messages = initialMessages;

    // Filter by message type
    if (selectedMessageType !== 'all') {
      messages = messages.filter(
        (msg: Message) => msg.message_type === selectedMessageType,
      );
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      messages = messages.filter(
        (msg: Message) =>
          msg.content.toLowerCase().includes(query) ||
          msg.subject?.toLowerCase().includes(query) ||
          msg.from_number?.includes(query) ||
          msg.to_numbers?.some((num: string) => num.includes(query)),
      );
    }

    // Sort by timestamp (newest first)
    const sortedMessages = [...messages].sort(
      (a: Message, b: Message) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );

    // Calculate stats
    const stats = conversation.messages.reduce(
      (acc: Record<string, number>, msg: Message) => {
        acc[msg.message_type] = (acc[msg.message_type] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    return {
      filteredMessages: sortedMessages,
      messageStats: stats,
    };
  }, [conversation?.messages, searchQuery, selectedMessageType]);

  if (!conversation) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-6 text-center">
        <div className="rounded-full bg-gray-100 p-3 mb-3">
          <ChatBubbleLeftRightIcon className="h-6 w-6 text-gray-400" />
        </div>
        <h3 className="text-lg font-medium text-gray-700 mb-1">
          No Conversation Data
        </h3>
        <p className="text-sm text-gray-500">
          The conversation data could not be loaded.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white border border-gray-200 rounded-md shadow-sm overflow-hidden">
      {/* Header */}
      <ConversationHeader
        conversation={conversation}
        isClientSms={conversation.is_client_sms_conversation}
      />

      {/* Stats */}
      <ConversationStats
        messageStats={messageStats}
        totalMessages={conversation.messages.length}
        filteredCount={filteredMessages.length}
      />

      {/* Search and Filters */}
      <MessageSearch
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        selectedMessageType={selectedMessageType}
        onMessageTypeChange={setSelectedMessageType}
        messageStats={messageStats}
      />

      {/* Message List */}
      <div className="flex-1 overflow-hidden">
        <MessageList messages={filteredMessages} searchQuery={searchQuery} />
      </div>
    </div>
  );
};
